import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Loader2, Plus, Trash2, Save, Eye, EyeOff, Pencil, X,
  BookOpen, GraduationCap, FileText, ChevronDown, ChevronUp,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SampleMocksManager({ darkMode: dm }) {
  const [mocks, setMocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [customSubject, setCustomSubject] = useState("");
  const [showSubjectInput, setShowSubjectInput] = useState(false);
  const [customClassLevel, setCustomClassLevel] = useState("");
  const [showClassLevelInput, setShowClassLevelInput] = useState(false);

  const DEFAULT_SUBJECTS = ["Mathematics", "English", "Science", "Social Studies", "Computing", "French", "RME", "Creative Arts"];
  const DEFAULT_CLASS_LEVELS = ["Basic 7", "Basic 8", "Basic 9"];

  // Collect all unique subjects from existing mocks + defaults
  const allSubjects = [...new Set([...DEFAULT_SUBJECTS, ...mocks.map(m => m.subject).filter(Boolean)])].sort();
  // Collect all unique class levels from existing mocks + defaults
  const allClassLevels = [...new Set([...DEFAULT_CLASS_LEVELS, ...mocks.map(m => m.class_level).filter(Boolean)])].sort();

  const headers = { Authorization: `Bearer ${localStorage.getItem("gmp_token")}` };

  const fetchMocks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/sample-mocks`, { headers });
      setMocks(res.data);
    } catch { toast.error("Failed to load sample mocks"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMocks(); }, [fetchMocks]);

  const startNew = () => {
    setEditingId("new");
    setForm({
      title: "", subject: "Mathematics", class_level: "Basic 9", difficulty: "medium",
      description: "", is_active: true, max_preview_questions: 5,
      questions: [{ id: "q1", question_number: 1, question: "", options: ["", "", "", ""], correct_answer: null }],
    });
  };

  const startEdit = (mock) => {
    setEditingId(mock.id);
    setForm({ ...mock, questions: mock.questions || [] });
  };

  const cancel = () => { setEditingId(null); setForm(null); };

  const addQuestion = () => {
    const num = (form.questions?.length || 0) + 1;
    setForm(f => ({
      ...f,
      questions: [...(f.questions || []), { id: `q${num}`, question_number: num, question: "", options: ["", "", "", ""] }],
    }));
  };

  const removeQuestion = (idx) => {
    setForm(f => ({
      ...f,
      questions: f.questions.filter((_, i) => i !== idx).map((q, i) => ({ ...q, question_number: i + 1 })),
    }));
  };

  const updateQuestion = (idx, field, value) => {
    setForm(f => {
      const qs = [...f.questions];
      qs[idx] = { ...qs[idx], [field]: value };
      return { ...f, questions: qs };
    });
  };

  const updateOption = (qIdx, oIdx, value) => {
    setForm(f => {
      const qs = [...f.questions];
      const opts = [...qs[qIdx].options];
      opts[oIdx] = value;
      qs[qIdx] = { ...qs[qIdx], options: opts };
      return { ...f, questions: qs };
    });
  };

  const saveMock = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.questions.length) { toast.error("Add at least one question"); return; }
    setSaving(true);
    try {
      if (editingId === "new") {
        await axios.post(`${API}/admin/sample-mocks`, form, { headers });
        toast.success("Sample mock created");
      } else {
        await axios.put(`${API}/admin/sample-mocks/${editingId}`, form, { headers });
        toast.success("Sample mock updated");
      }
      cancel();
      fetchMocks();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const deleteMock = async (id) => {
    if (!window.confirm("Delete this sample mock?")) return;
    try {
      await axios.delete(`${API}/admin/sample-mocks/${id}`, { headers });
      toast.success("Deleted");
      fetchMocks();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleActive = async (mock) => {
    try {
      await axios.put(`${API}/admin/sample-mocks/${mock.id}`, { is_active: !mock.is_active }, { headers });
      fetchMocks();
      toast.success(mock.is_active ? "Deactivated" : "Activated");
    } catch { toast.error("Failed to toggle"); }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  // ── FORM VIEW ──
  if (form) {
    return (
      <div className="space-y-4" data-testid="sample-mock-form">
        <div className="flex items-center justify-between">
          <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
            {editingId === "new" ? "Create Sample Mock" : "Edit Sample Mock"}
          </h3>
          <Button variant="ghost" size="sm" onClick={cancel} className={dm ? "text-gray-400" : ""}>
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
        </div>

        <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Title</label>
                <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. BECE Mathematics Sample 2026"
                  className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                  data-testid="sample-mock-title" />
              </div>
              <div>
                <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Subject</label>
                {!showSubjectInput ? (
                  <div className="flex gap-2">
                    <Select value={form.subject} onValueChange={v => { setForm(f => ({ ...f, subject: v })); setShowSubjectInput(false); }}>
                      <SelectTrigger className={`rounded-xl flex-1 ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} data-testid="sample-mock-subject">
                        <SelectValue placeholder="Select subject" />
                      </SelectTrigger>
                      <SelectContent>
                        {allSubjects.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowSubjectInput(true)}
                      className={`rounded-xl shrink-0 ${dm ? "border-gray-600 text-gray-300" : ""}`}
                      title="Add custom subject" data-testid="add-custom-subject-btn">
                      <Plus className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={customSubject}
                      onChange={e => setCustomSubject(e.target.value)}
                      placeholder="Type a new subject name..."
                      className={`rounded-xl flex-1 ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                      autoFocus
                      data-testid="custom-subject-input"
                    />
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => {
                        if (customSubject.trim()) {
                          setForm(f => ({ ...f, subject: customSubject.trim() }));
                          setCustomSubject("");
                          setShowSubjectInput(false);
                        }
                      }}
                      className={`rounded-xl shrink-0 ${dm ? "border-gray-600 text-gray-300" : ""}`}
                      data-testid="confirm-custom-subject-btn">
                      <Save className="w-3.5 h-3.5" />
                    </Button>
                    <Button type="button" size="sm" variant="ghost"
                      onClick={() => { setShowSubjectInput(false); setCustomSubject(""); }}
                      className={`rounded-xl shrink-0 ${dm ? "text-gray-400" : ""}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className={`text-xs font-semibold ${dm ? "text-gray-300" : "text-gray-600"}`}>Class Level</label>
                  <button type="button" onClick={() => setShowClassLevelInput(!showClassLevelInput)} className="text-xs text-accent hover:underline">
                    {showClassLevelInput ? "Cancel" : "+ Add New"}
                  </button>
                </div>
                {showClassLevelInput && (
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={customClassLevel}
                      onChange={e => setCustomClassLevel(e.target.value)}
                      placeholder="e.g. Basic 10"
                      className={`rounded-xl text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                      data-testid="custom-class-level-input"
                    />
                    <Button type="button" size="sm" onClick={() => {
                      const val = customClassLevel.trim();
                      if (val && !allClassLevels.includes(val)) {
                        setForm(f => ({ ...f, class_level: val }));
                        setCustomClassLevel("");
                        setShowClassLevelInput(false);
                      }
                    }} className="rounded-xl" data-testid="add-class-level-btn">Add</Button>
                  </div>
                )}
                <Select value={form.class_level} onValueChange={v => setForm(f => ({ ...f, class_level: v }))}>
                  <SelectTrigger className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...new Set([...allClassLevels, form.class_level].filter(Boolean))].sort().map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Difficulty</label>
                <Select value={form.difficulty || "medium"} onValueChange={v => setForm(f => ({ ...f, difficulty: v }))}>
                  <SelectTrigger className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} data-testid="sample-mock-difficulty">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="easy">Easy (90s/question)</SelectItem>
                    <SelectItem value="medium">Medium (60s/question)</SelectItem>
                    <SelectItem value="hard">Hard (45s/question)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Max Preview Questions</label>
                <Input type="number" min={1} max={20} value={form.max_preview_questions}
                  onChange={e => setForm(f => ({ ...f, max_preview_questions: parseInt(e.target.value) || 5 }))}
                  className={`rounded-xl ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`}
                  data-testid="sample-mock-max-preview" />
              </div>
            </div>
            <div>
              <label className={`text-xs font-semibold mb-1.5 block ${dm ? "text-gray-300" : "text-gray-600"}`}>Description</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={2} placeholder="Brief description of this sample exam..."
                className={`w-full rounded-xl border px-3 py-2 text-sm ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : "border-gray-200"}`}
                data-testid="sample-mock-description" />
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className={`text-sm ${dm ? "text-gray-100" : ""}`}>
                <FileText className="w-4 h-4 inline mr-1.5" />
                Questions ({form.questions.length})
              </CardTitle>
              <Button size="sm" variant="outline" onClick={addQuestion}
                className={`rounded-lg text-xs ${dm ? "border-gray-600 text-gray-300" : ""}`} data-testid="add-question-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add Question
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
            {form.questions.map((q, qi) => (
              <div key={qi} className={`p-4 rounded-xl border ${dm ? "bg-gray-900/50 border-gray-700" : "bg-gray-50 border-gray-200"}`}
                data-testid={`question-${qi}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-xs font-bold ${dm ? "text-gray-200" : "text-gray-700"}`}>Q{qi + 1}</span>
                  {form.questions.length > 1 && (
                    <Button size="sm" variant="ghost" onClick={() => removeQuestion(qi)} className="text-red-500 h-6 w-6 p-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                <textarea value={q.question} onChange={e => updateQuestion(qi, "question", e.target.value)}
                  rows={2} placeholder="Enter the question text..."
                  className={`w-full rounded-lg border px-2 py-1.5 text-xs mb-3 ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : "border-gray-200"}`} />
                <div className="grid grid-cols-2 gap-2">
                  {(q.options || []).map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-1.5">
                      <button type="button" title={`Mark as correct answer`}
                        onClick={() => updateQuestion(qi, "correct_answer", oi)}
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                          q.correct_answer === oi
                            ? "border-emerald-500 bg-emerald-500 text-white"
                            : dm ? "border-gray-500 hover:border-emerald-400" : "border-gray-300 hover:border-emerald-400"
                        }`}>
                        {q.correct_answer === oi && <span className="text-[9px] font-bold">✓</span>}
                      </button>
                      <Input value={opt} onChange={e => updateOption(qi, oi, e.target.value)}
                        placeholder={`Option ${String.fromCharCode(65 + oi)}`}
                        className={`rounded-lg h-8 text-xs flex-1 ${dm ? "bg-gray-700 border-gray-600 text-gray-100" : ""}`} />
                    </div>
                  ))}
                </div>
                {q.correct_answer == null && (
                  <p className="text-[10px] text-amber-500 mt-1.5">Click the circle to mark the correct answer</p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={cancel} className={`rounded-xl ${dm ? "border-gray-600 text-gray-300" : ""}`}>Cancel</Button>
          <Button onClick={saveMock} disabled={saving}
            className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl" data-testid="save-sample-mock-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
            {editingId === "new" ? "Create Sample Mock" : "Save Changes"}
          </Button>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  return (
    <div className="space-y-4" data-testid="sample-mocks-manager">
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-bold ${dm ? "text-gray-100" : "text-gray-900"}`}>
            Sample Mocks for Preview
          </h3>
          <p className={`text-xs mt-0.5 ${dm ? "text-gray-400" : "text-gray-500"}`}>
            Upload sample exams that visitors can preview before signing up. Separate from paid content.
          </p>
        </div>
        <Button size="sm" onClick={startNew}
          className="bg-[#e0482d] hover:bg-[#c93d25] text-white rounded-xl text-xs" data-testid="create-sample-mock-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New Sample Mock
        </Button>
      </div>

      {mocks.length === 0 ? (
        <Card className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}>
          <CardContent className="py-12 text-center">
            <BookOpen className={`w-10 h-10 mx-auto mb-3 opacity-30 ${dm ? "text-gray-400" : "text-gray-400"}`} />
            <p className={`text-sm font-medium ${dm ? "text-gray-300" : "text-gray-600"}`}>No sample mocks yet</p>
            <p className={`text-xs mt-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>Create sample exams for visitors to preview</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {mocks.map(mock => (
            <Card key={mock.id} className={`rounded-2xl border ${dm ? "bg-gray-800 border-gray-700" : "border-gray-200"}`}
              data-testid={`sample-mock-${mock.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${dm ? "bg-blue-900/30" : "bg-blue-50"}`}>
                      <GraduationCap className={`w-5 h-5 ${dm ? "text-blue-400" : "text-blue-600"}`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`font-semibold text-sm ${dm ? "text-gray-100" : "text-gray-900"}`}>{mock.title}</span>
                        <Badge className={`text-[10px] ${mock.is_active ? "bg-emerald-500/15 text-emerald-600" : "bg-gray-500/15 text-gray-500"}`}>
                          {mock.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className={`flex items-center gap-3 text-xs ${dm ? "text-gray-400" : "text-gray-500"}`}>
                        <span>{mock.subject}</span>
                        <span>{mock.class_level}</span>
                        <Badge className={`text-[10px] ${
                          mock.difficulty === "hard" ? "bg-red-500/15 text-red-500" :
                          mock.difficulty === "easy" ? "bg-emerald-500/15 text-emerald-500" :
                          "bg-blue-500/15 text-blue-500"
                        }`}>{(mock.difficulty || "medium").charAt(0).toUpperCase() + (mock.difficulty || "medium").slice(1)}</Badge>
                        <span>{(mock.questions || []).length} questions</span>
                        <span>Preview: {mock.max_preview_questions} max</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => toggleActive(mock)}
                      className={`h-8 w-8 p-0 rounded-lg ${dm ? "border-gray-600 text-gray-300" : ""}`}
                      title={mock.is_active ? "Deactivate" : "Activate"}>
                      {mock.is_active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEdit(mock)}
                      className={`h-8 w-8 p-0 rounded-lg ${dm ? "border-gray-600 text-gray-300" : ""}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => deleteMock(mock.id)}
                      className="h-8 w-8 p-0 rounded-lg border-red-200 text-red-500 hover:bg-red-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Expandable preview */}
                <button onClick={() => setExpandedId(expandedId === mock.id ? null : mock.id)}
                  className={`flex items-center gap-1 mt-2 text-xs font-medium ${dm ? "text-gray-400 hover:text-gray-300" : "text-gray-500 hover:text-gray-700"}`}>
                  {expandedId === mock.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {expandedId === mock.id ? "Hide" : "Preview"} questions
                </button>
                {expandedId === mock.id && (
                  <div className={`mt-3 space-y-2 pl-2 border-l-2 ${dm ? "border-gray-700" : "border-gray-200"}`}>
                    {(mock.questions || []).slice(0, 5).map((q, i) => (
                      <div key={i} className={`text-xs ${dm ? "text-gray-300" : "text-gray-700"}`}>
                        <span className="font-bold">Q{i + 1}:</span> {q.question}
                        {q.options?.length > 0 && (
                          <div className={`ml-4 mt-1 ${dm ? "text-gray-500" : "text-gray-400"}`}>
                            {q.options.map((o, oi) => <div key={oi}>{String.fromCharCode(65 + oi)}. {o}</div>)}
                          </div>
                        )}
                      </div>
                    ))}
                    {(mock.questions || []).length > 5 && (
                      <p className={`text-xs italic ${dm ? "text-gray-500" : "text-gray-400"}`}>
                        ...and {(mock.questions || []).length - 5} more
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
